/**
 * Le bandeau « une rédaction est en cours » (#859).
 *
 * **Pourquoi il est non négociable** (carte #823) : depuis #857 une rédaction
 * survit à la navigation et continue d'écrire dans un canvas parqué hors écran.
 * Sans ce bandeau, un travail qui tourne ailleurs est purement invisible — et
 * son texte, qui ne passe par aucun tampon serveur, se perd si l'onglet ferme.
 * C'est lui qui rend le hors-champ visible.
 *
 * **Pourquoi ici, entre la navigation et la liste** (`AppSidebar`) : il est
 * au-dessus de la zone qui défile, donc il ne peut pas s'échapper par le haut
 * quand l'avocat fait défiler ses conversations ; et il ne rend **rien** au
 * repos, donc la liste ne lui cède ses ~44 px que pendant qu'une rédaction
 * tourne vraiment. C'est la contrainte du ticket : ne pas écraser la liste,
 * dont la largeur est désormais assumée en permanence (#826).
 *
 * **Il survit au repli en rail.** Replier la barre latérale masque la liste des
 * conversations, donc le signal porté par la ligne de conversation disparaît :
 * le bandeau devient alors la seule chose qui dit qu'un travail tourne. Il se
 * réduit à sa pastille, il ne s'éteint pas.
 */

import * as React from 'react';
import { PenLine } from 'lucide-react';
import { useGlobalConversationList } from '@/features/hector-agent-casediscovery-v3/hooks/useGlobalConversationList';
import { cn } from '@/lib/utils';
import { useConversationsQuiRedigent } from '../streaming/useConversationsQuiRedigent';
import { nomAffichableDeConversation } from '../state/canvasHolder';

/** Même projection que la liste : une autre valeur créerait une seconde requête. */
const PROJECTION_ARCHIVE = { archived: 'include' } as const;

/** Le nom manque (liste en cours d'arrivée, fil que le serveur n'a pas nommé). */
const NOM_INCONNU = 'une conversation';

interface RedactionEnCoursBannerProps {
  /** Barre latérale repliée en rail : plus de place que pour la pastille. */
  collapsed: boolean;
}

export function RedactionEnCoursBanner({
  collapsed,
}: RedactionEnCoursBannerProps): React.JSX.Element {
  const quiRedigent = useConversationsQuiRedigent();
  const { conversations, selectConversation } = useGlobalConversationList(PROJECTION_ARCHIVE);

  const noms = React.useMemo(
    () => quiRedigent.map((id) => ({
      id,
      nom: nomAffichableDeConversation(conversations.find((c) => c.id === id)?.title),
    })),
    [quiRedigent, conversations],
  );

  const seule = noms.length === 1 ? noms[0] : null;
  const resume = seule
    ? `Rédaction en cours dans ${seule.nom ? `« ${seule.nom} »` : NOM_INCONNU}.`
    : `${noms.length} rédactions en cours.`;

  // **La région live ne contient QUE son énoncé.** Elle est montée en permanence
  // et vide au repos — une région insérée en même temps que son contenu n'est pas
  // énoncée de façon fiable (leçon de l'annonce du bail, #855). Mais elle ne doit
  // pas envelopper le visuel : celui-ci est découpé pour la mise en page (un
  // libellé, puis un bouton par conversation) et serait énoncé morceau par
  // morceau AVANT la phrase, qu'il ferait alors passer pour une répétition.
  return (
    <div className={collapsed ? undefined : 'px-2'}>
      {noms.length === 0 ? null : collapsed ? (
        <div
          className="flex h-9 items-center justify-center"
          title={`${resume} Ne fermez pas cet onglet.`}
        >
          <PenLine className="h-4 w-4 animate-pulse text-[#003D82]" aria-hidden />
        </div>
      ) : (
        <div className="rounded-lg bg-[#003D82]/[0.06] px-2 py-1.5">
          <div className="flex items-center gap-1.5">
            <PenLine className="h-3.5 w-3.5 shrink-0 animate-pulse text-[#003D82]" aria-hidden />
            <span className="truncate text-[12px] font-medium leading-tight text-[#003D82]">
              {seule ? 'Rédaction en cours' : `${noms.length} rédactions en cours`}
            </span>
          </div>
          {/* Le nom du fil est un LIEN de retour, pas une étiquette : c'est le
              seul chemin vers la rédaction quand ce n'est pas le fil affiché.
              Même rôle que la pastille du porteur dans le bandeau du canvas. */}
          <div className="mt-0.5 flex flex-col items-start">
            {noms.map(({ id, nom }) => (
              <button
                key={id}
                type="button"
                onClick={() => selectConversation(id)}
                aria-label={`Rejoindre la rédaction en cours dans ${nom ? `« ${nom} »` : NOM_INCONNU}`}
                className={cn(
                  'max-w-full truncate rounded text-[11px] leading-tight text-grey-600',
                  'hover:text-[#003D82] hover:underline',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#003D82]/40',
                )}
              >
                {nom ?? NOM_INCONNU}
              </button>
            ))}
          </div>
        </div>
      )}
      <span role="status" aria-live="polite" className="sr-only">
        {noms.length === 0 ? '' : `${resume} Ne fermez pas cet onglet.`}
      </span>
    </div>
  );
}
